import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import uniqid from 'uniqid';
import {
  Box, TableBody, TableCell, TableHeader, TableRow,
} from 'grommet';
import { useAppTheme } from '@Components/Context';
import { initiateCompanySettingsRequest, updateCompanyDomainRequest } from '@Actions';
import { AppButton, FlexibleSelectDropdown } from '@Components/Control';
import { ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import { DetailTableRow, DomainStatusInput } from './ReviewTableComponents';
import {
  renderTableHeader,
  tablePadding,
  NoDataPlaceholder,
  TableWrapper,
} from '../SharedComponents';
import { StyledDetailTable } from './StyledDashboard';

const ReviewTable = ({
  size,
  loading,
  reviewData = null,
  reviewDataHeaders = null,
  reviewStatusOptions = null,
  initializeReviewedRows,
  updateRowReviewStatus,
  updateMultipleRowsReviewStatus,
  bulkUpdateReviewStatusByDomain = null,
  selectedStatuses = null,
  handleQuery = null,
  handleResultsPerPage = () => null,
  resultsPerPageOptions = null,
  resultsPerPage = null,
  handlePage = null,
  handleFilter = null,
  handleExportCSV = null,
  tableControls = null,
  currentPage = null,
  totalResults = null,
  companySettings = null,
  fetchCompanySettings,
  updateCompanyDomain,
  searchQuery,
  showRefresh = false,
  handleRefresh = null,
  customHeader = null,
  noDataMessage = null,
  paymentPending,
  watermarkGroupingKey = null,
  onSelectionChange = null,
  hostingData = null,
  includeISPData = false,
}) => {
  const [data, setData] = React.useState(null);
  const [selectedRowIds, setSelectedRowIds] = React.useState([]);
  const { theme: { page, button } } = useAppTheme();

  React.useEffect(() => {
    if (!companySettings) fetchCompanySettings();
  }, [companySettings]);

  React.useEffect(() => {
    setSelectedRowIds([]);
    if (reviewData?.length > 0) {
      // Normalize rows from {id, cells} format to {id, cells} shape consistently
      const normalized = reviewData.map((row) => (Array.isArray(row)
        ? { id: null, cells: row }
        : { id: row.id || null, cells: row.cells || row }
      ));
      setData(normalized);
      initializeReviewedRows(normalized);
    } else {
      setData(reviewData);
    }
  }, [reviewData]);

  React.useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(selectedRowIds);
    }
  }, [selectedRowIds, onSelectionChange]);

  const dmcaListsSetting = companySettings?.find((setting) => setting.api.includes('dmca'));
  const dmcaListsEnabled = Boolean(dmcaListsSetting);

  const handleRowSelect = (rowEditableId) => {
    if (selectedRowIds.includes(rowEditableId)) {
      setSelectedRowIds(selectedRowIds.filter((id) => id !== rowEditableId));
    } else {
      setSelectedRowIds([...selectedRowIds, rowEditableId]);
    }
  };

  const handleDomainUpdate = (settingKey, value, action = 'push') => {
    const body = { data: [{ value, action }] };

    const displaySetting = settingKey.includes('deny')
      ? 'DMCA denylist'
      : 'DMCA allowlist';
    const successNotice = action === 'push'
      ? `Domain added to ${displaySetting}`
      : `Domain removed from ${displaySetting}`;

    updateCompanyDomain(settingKey, body, null, () => null, successNotice);
  };

  const isSmall = size.includes('small');
  const displayHeader = isSmall ? _.filter(reviewDataHeaders, (({ display }) => display !== 'Review Status')) : reviewDataHeaders;

  const calculateColumnWidths = (headerLength) => {
    const checkboxWidth = 3;
    const remainingWidth = 100 - checkboxWidth;
    const dynamicWidth = headerLength > 0 ? remainingWidth / headerLength : remainingWidth;

    if (isSmall) {
      return {
        checkboxWidth: 'auto',
        dynamicWidth: 'auto',
      };
    }

    return {
      checkboxWidth: `${checkboxWidth}%`,
      dynamicWidth: `${dynamicWidth}%`,
    };
  };

  const renderTableContents = () => {
    if (!reviewData || (reviewData && reviewData.length === 0) || loading) {
      const showRefreshButton = reviewData && reviewData.length === 0
        && (searchQuery?.filters?.length > 0 || showRefresh);
      const noDataText = noDataMessage ?? 'Searching for infringements, check back soon';
      const label = showRefreshButton ? 'No results found for this query' : noDataText;

      return (
        <NoDataPlaceholder
          noRound
          noShadow
          showRefreshButton={showRefreshButton}
          refreshQuery={() => (handleRefresh
            ? handleRefresh()
            : handleFilter(null, null, null, false, true)
          )}
          buttonHighlight={button.primaryBg}
          label={label}
          backgroundColor={page.tableBg}
          loading={loading || !reviewData}
        />
      );
    }

    const { checkboxWidth, dynamicWidth } = calculateColumnWidths(displayHeader.length);

    const allEditableRows = data && data
      .map((row) => row.cells.find((item) => item.editable === 'dropdown' && item.editableId))
      .filter((item) => item?.editableId);

    const allEditableIds = allEditableRows?.map((item) => item.editableId);
    const hasResultIds = !isSmall && data && data.some((row) => row.id !== null);

    return (
      <StyledDetailTable>
        <TableHeader>
          <TableRow>
            {!isSmall && allEditableIds?.length > 0 && (
              <TableCell key={uniqid()} scope="col" align="start" size={checkboxWidth} style={{ marginLeft: '-11px' }}>
                <ProductFormCheckBoxInput
                  small={isSmall}
                  key={uniqid()}
                  bgColor={page.inputBg}
                  borderColor={page.inputBorder}
                  checkmarkColor={page.highlight}
                  focusHighlight={page.focusHighlight}
                  value={selectedRowIds?.length === allEditableIds?.length}
                  handleFormValues={() => {
                    if (selectedRowIds?.length === allEditableIds?.length) {
                      setSelectedRowIds([]);
                    } else {
                      setSelectedRowIds(allEditableIds);
                    }
                  }}
                  customBoxSize="1.25rem"
                  customCheckmarkSize="1.1rem"
                />
              </TableCell>
            )}
            {displayHeader && displayHeader.map(({ display }) => (
              <TableCell key={uniqid()} scope="col" align="start" size={dynamicWidth}>
                {renderTableHeader(display, isSmall)}
              </TableCell>
            ))}
            {hasResultIds && (
              <TableCell key={uniqid()} scope="col" align="end" size="auto" />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.length > 0 && data.map((rowData) => (
            <DetailTableRow
              key={uniqid()}
              isSmall={isSmall}
              header={displayHeader}
              rowData={rowData.cells}
              resultId={rowData.id}
              updateRowReviewStatus={updateRowReviewStatus}
              selectedStatuses={selectedStatuses}
              handleRowSelect={handleRowSelect}
              selectedRowIds={selectedRowIds}
              showSpacerElement={allEditableIds?.length > 0}
              dmcaListsEnabled={dmcaListsEnabled}
              handleDomainUpdate={handleDomainUpdate}
              watermarkGroupingKey={watermarkGroupingKey}
              hostingData={hostingData}
              includeISPData={includeISPData}
            />
          ))}
        </TableBody>
      </StyledDetailTable>
    );
  };

  return (
    <Box direction="column">
      <TableWrapper
        small={isSmall}
        header={customHeader ?? 'Suspected Infringements'}
        boxPad={tablePadding(isSmall)}
        handleResultsPerPage={handleResultsPerPage}
        handlePage={handlePage}
        resultsPerPageOptions={resultsPerPageOptions}
        selectedResultsCount={resultsPerPage}
        hidePagination={!reviewData || (reviewData && reviewData.length === 0) || loading}
        currentPage={currentPage}
        currentResults={reviewData?.length}
        isPaged={handlePage && (totalResults > data?.length)}
        totalResults={totalResults}
        tableControls={(
          <Box
            wrap
            direction="row"
            gap="0.5rem"
            align={isSmall ? 'end' : 'center'}
          >
            {tableControls && tableControls}
            {selectedRowIds.length > 0 && (
              <Box pad={{ vertical: isSmall ? '0.5rem' : '0' }}>
                <FlexibleSelectDropdown
                  buttonWidth="9rem"
                  buttonJustify="between"
                  flexibleOptions={reviewStatusOptions || []}
                  selectedOption={null}
                  handleItemSelect={(val) => {
                    updateMultipleRowsReviewStatus(selectedRowIds, val);
                    setSelectedRowIds([]);
                  }}
                  textColor={page.inputText}
                  displayKey="display"
                  valueKey="api"
                  noSelectionLabel="Apply bulk status"
                />
              </Box>
            )}
            <Box direction="row" gap="0.5rem" pad={{ vertical: isSmall ? '0.5rem' : '0' }} align="center">
              {bulkUpdateReviewStatusByDomain && (
                <DomainStatusInput
                  small={isSmall}
                  bulkUpdateReviewStatusByDomain={bulkUpdateReviewStatusByDomain}
                  reviewStatusOptions={reviewStatusOptions || []}
                />
              )}
              {handleExportCSV && (
                <Box>
                  <AppButton
                    overrideHover
                    onClick={() => handleExportCSV()}
                    height="var(--table-controls-height)"
                    label="Export CSV"
                    level="dynamicSmall"
                    color={button.primaryText}
                    bgColor={button.primaryBg}
                    disabled={paymentPending}
                  />
                </Box>
              )}
            </Box>
            {searchQuery && (
              <Box pad={{ vertical: isSmall ? '0.5rem' : '0' }}>
                <AppButton
                  overrideHover
                  disabled={(
                    Object.entries(searchQuery)
                      .filter(([key]) => key !== 'paging' && key !== 'id')
                      .every(([, value]) => !value || (typeof value === 'object' && Object.keys(value).length === 0))
                  )}
                  onClick={() => handleQuery('reset', null, null)}
                  level="tableControls"
                  height="var(--table-controls-height)"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  label="Reset"
                />
              </Box>
            )}
          </Box>
        )}
      >
        {renderTableContents()}
      </TableWrapper>
    </Box>
  );
};

function mapStateToProps(state) {
  const { companySettings } = state.companySettings;

  return { companySettings };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCompanySettings: initiateCompanySettingsRequest,
    updateCompanyDomain: updateCompanyDomainRequest,
  }, dispatch);
}

/* eslint-disable camelcase */
ReviewTable.propTypes = {
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'mediumlarge', 'large']).isRequired,
  loading: PropTypes.bool.isRequired,
  reviewData: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      api: PropTypes.string,
      decoration: PropTypes.any,
      display: PropTypes.any,
      editable: PropTypes.string,
      editableId: PropTypes.string,
      editableOptions: PropTypes.arrayOf(PropTypes.shape({
        api: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
      })),
      unit: PropTypes.string,
    })),
    PropTypes.shape({
      id: PropTypes.string,
      cells: PropTypes.array,
    }),
  ])),
  reviewDataHeaders: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
  })),
  reviewStatusOptions: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  selectedStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    review_status: PropTypes.string.isRequired,
    prev_status: PropTypes.string,
  })),
  initializeReviewedRows: PropTypes.func.isRequired,
  updateRowReviewStatus: PropTypes.func.isRequired,
  updateMultipleRowsReviewStatus: PropTypes.func.isRequired,
  fetchCompanySettings: PropTypes.func.isRequired,
  updateCompanyDomain: PropTypes.func.isRequired,
  companySettings: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  bulkUpdateReviewStatusByDomain: PropTypes.func,
  handleResultsPerPage: PropTypes.func,
  resultsPerPageOptions: PropTypes.arrayOf(PropTypes.number.isRequired),
  resultsPerPage: PropTypes.number,
  totalResults: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  handleQuery: PropTypes.func,
  handlePage: PropTypes.func,
  handleFilter: PropTypes.func,
  handleExportCSV: PropTypes.func,
  currentPage: PropTypes.number,
  tableControls: PropTypes.node,
  showRefresh: PropTypes.bool,
  handleRefresh: PropTypes.func,
  searchQuery: PropTypes.shape({
    paging: PropTypes.shape({
      to: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
    }),
    sort: PropTypes.shape({
      name: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
    }),
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any,
      predicate: PropTypes.string.isRequired,
    })),
  }),
  customHeader: PropTypes.string,
  noDataMessage: PropTypes.string,
  paymentPending: PropTypes.bool.isRequired,
  watermarkGroupingKey: PropTypes.string,
  onSelectionChange: PropTypes.func,
  hostingData: PropTypes.arrayOf(PropTypes.shape({ host: PropTypes.string })),
  includeISPData: PropTypes.bool,
};
/* eslint-enable camelcase */

export default connect(mapStateToProps, mapDispatchToProps)(ReviewTable);
